const safeArray = (value) => (Array.isArray(value) ? value : []);
const MISSING_RELATIONAL_RECIPE_CODES = new Set(['42P01', 'PGRST202', 'PGRST205']);
const RELATIONAL_RECIPE_SESSION_KEY = 'impulza_relational_recipes_available';

// La tabla relacional `recipes`/`recipe_items` NO existe en la base actual.
// Sondearla solo genera un 404 en consola (ej. /sistema/produccion-registrar).
// Queda desactivada por defecto y se puede reactivar para orgs legacy con
// VITE_ENABLE_LEGACY_RELATIONAL_RECIPES=true.
const LEGACY_RELATIONAL_RECIPES_ENABLED =
  String(import.meta.env?.VITE_ENABLE_LEGACY_RELATIONAL_RECIPES || '').toLowerCase() === 'true';

let relationalRecipesAvailabilityCache = LEGACY_RELATIONAL_RECIPES_ENABLED ? null : false;

const readRelationalRecipesAvailability = () => {
  try {
    const raw = sessionStorage.getItem(RELATIONAL_RECIPE_SESSION_KEY);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch { /* storage no disponible */ }
  return null;
};

const writeRelationalRecipesAvailability = (value) => {
  try {
    sessionStorage.setItem(RELATIONAL_RECIPE_SESSION_KEY, value ? 'true' : 'false');
  } catch { /* storage no disponible */ }
};

export const isMissingRelationalRecipesError = (error) => {
  const message = String(error?.message || '').toLowerCase();
  const code = String(error?.code || '').toUpperCase();
  const status = Number(error?.status || 0);

  if (MISSING_RELATIONAL_RECIPE_CODES.has(code)) {
    return message.includes('recipes') || status === 404;
  }

  return status === 404 && message.includes('recipes');
};

/**
 * @deprecated La tabla relacional `recipes`/`recipe_items` quedo en desuso.
 * Hoy la fuente unica de verdad es la tabla `recetas` (JSON) que usa
 * Recetas.jsx. Esta funcion se mantiene como fallback defensivo para
 * organizaciones que aun tengan datos legacy en `recipes`.
 * Si confirmas que ningun cliente tiene datos ahi, se puede eliminar
 * junto con el `select * recipes` de los pages que la consultan.
 */
export const loadRelationalRecipesIfAvailable = async ({
  supabase,
  organizationId,
  select = 'id, product_id, yield, recipe_items(*)',
}) => {
  if (!LEGACY_RELATIONAL_RECIPES_ENABLED) return [];
  if (!organizationId) return [];

  if (relationalRecipesAvailabilityCache === false) return [];

  if (relationalRecipesAvailabilityCache === null) {
    const persistedValue = readRelationalRecipesAvailability();
    if (persistedValue !== null) {
      relationalRecipesAvailabilityCache = persistedValue;
    }
  }

  if (relationalRecipesAvailabilityCache === false) return [];

  const response = await supabase
    .from('recipes')
    .select(select)
    .eq('organization_id', organizationId);

  if (response.error) {
    if (isMissingRelationalRecipesError(response.error)) {
      relationalRecipesAvailabilityCache = false;
      writeRelationalRecipesAvailability(false);
      return [];
    }
    throw response.error;
  }

  relationalRecipesAvailabilityCache = true;
  writeRelationalRecipesAvailability(true);
  return response.data || [];
};

const safeYield = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

// FIX UX: clamp de merma 0-99 reutilizable
const safeMermaPct = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.min(99, parsed));
};

export const findRecipeDefinitionForProduct = ({
  productId,
  recetasJson = [],
  recipes = [],
}) => {
  if (!productId) return null;

  const jsonRecipe = safeArray(recetasJson).find((recipe) => recipe.producto_id === productId);
  if (jsonRecipe) {
    return {
      source: 'recetas',
      productId,
      productName: jsonRecipe.producto_nombre || '',
      ingredients: safeArray(jsonRecipe.ingredientes),
      yield: safeYield(jsonRecipe.rendimiento),
      // FIX UX: merma_pct viene del JSON de la receta nueva. La tabla relacional
      // legacy no tiene este campo → queda en 0.
      mermaPct: safeMermaPct(jsonRecipe.merma_pct),
    };
  }

  const relationalRecipe = safeArray(recipes).find((recipe) => recipe.product_id === productId);
  if (relationalRecipe) {
    return {
      source: 'recipes',
      productId,
      productName: relationalRecipe.products?.name || '',
      ingredients: safeArray(relationalRecipe.recipe_items),
      yield: safeYield(relationalRecipe.yield),
      mermaPct: 0,
    };
  }

  return null;
};

export const loadRecipeBomForSite = async ({
  supabase,
  productId,
  siteId,
  quantity = 1,
  recetasJson = [],
  recipes = [],
  // FIX UX: si la organización tiene `enable_recipe_merma=true`, el caller pasa
  // applyMerma=true y el costo unitario se calcula como `costoTotal / (qty * (1 - merma/100))`.
  // Si applyMerma=false (default), el cálculo es el legacy: costoTotal / qty.
  applyMerma = false,
}) => {
  const recipe = findRecipeDefinitionForProduct({
    productId,
    recetasJson,
    recipes,
  });

  if (!recipe) {
    return {
      hasRecipe: false,
      recipe,
      bom: [],
      costTotal: 0,
      costUnit: 0,
      mermaPct: 0,
      mermaApplied: false,
    };
  }

  const multiplier = Number(quantity || 0);
  const safeMultiplier = Number.isFinite(multiplier) && multiplier > 0 ? multiplier : 0;
  const recipeYield = safeYield(recipe.yield);
  const mermaPct = safeMermaPct(recipe.mermaPct || 0);
  const mermaApplied = applyMerma && mermaPct > 0;
  // Factor para escalar los ingredientes: cuanto se necesita para producir `quantity`
  // dado que la receta esta definida para `recipeYield` unidades por lote.
  const scaleFactor = recipeYield > 0 ? safeMultiplier / recipeYield : 0;
  const ingredientIds = recipe.ingredients
    .map((item) => String(recipe.source === 'recetas' ? item?.id || '' : item?.ingredient_id || '').trim())
    .filter(Boolean);

  if (ingredientIds.length === 0) {
    return {
      hasRecipe: true,
      recipe,
      bom: [],
      costTotal: 0,
      costUnit: 0,
    };
  }

  const [stockRes, productsRes] = await Promise.all([
    supabase
      .from('stock_levels')
      .select('product_id, quantity')
      .eq('site_id', siteId)
      .in('product_id', ingredientIds),
    supabase
      .from('products')
      .select('id, name, cost_price, descuenta_stock_produccion, units_of_measure(abbreviation)')
      .in('id', ingredientIds),
  ]);

  if (stockRes.error) throw stockRes.error;
  if (productsRes.error) throw productsRes.error;

  const stockMap = (stockRes.data || []).reduce((map, item) => {
    const productKey = String(item?.product_id || '').trim();
    if (!productKey) return map;

    map.set(productKey, Number(map.get(productKey) || 0) + Number(item?.quantity || 0));
    return map;
  }, new Map());
  const productMap = new Map((productsRes.data || []).map((item) => [item.id, item]));

  const bom = recipe.ingredients.map((ingredient) => {
    const id = recipe.source === 'recetas' ? ingredient?.id : ingredient?.ingredient_id;
    const productRow = productMap.get(id) || null;
    const baseQuantity = Number(ingredient?.quantity || 0);
    // baseQuantity esta expresado para producir un lote (recipeYield unidades).
    // Para producir `quantity` unidades, escalamos por `quantity / recipeYield`.
    const needed = baseQuantity * scaleFactor;
    const stockActual = Number(stockMap.get(id) || 0);
    const costPrice = Number(productRow?.cost_price || 0);
    // Insumos de solo costo (mano de obra, agua, energia): suman al costo de la
    // produccion pero no se validan ni descuentan de stock. La RPC decide con el
    // mismo flag; esto es solo para que la UI no los marque como faltantes.
    const descuentaStock = productRow?.descuenta_stock_produccion !== false;

    return {
      id,
      nombre:
        productRow?.name ||
        (recipe.source === 'recetas' ? ingredient?.nombre : ingredient?.ingredient_name) ||
        'Insumo',
      um: productRow?.units_of_measure?.abbreviation || 'un',
      necesario: needed,
      stockActual,
      descuentaStock,
      falta: descuentaStock && stockActual < needed,
      costoUnitarioInsumo: costPrice,
    };
  });

  const costTotal = bom.reduce((acc, item) => acc + Number(item.costoUnitarioInsumo || 0) * Number(item.necesario || 0), 0);

  // FIX UX: si la merma está activa para la org y la receta declara una,
  // el divisor "real" es la cantidad esperada de unidades buenas
  // (qty * (1 - merma/100)). Esto coincide con el cálculo de Recetas.jsx
  // y evita que el cost_price del PT registrado en producción sea menor
  // al cost_price guardado en la ficha del producto.
  const effectiveDivisor = mermaApplied
    ? safeMultiplier * (1 - mermaPct / 100)
    : safeMultiplier;
  const costUnit = effectiveDivisor > 0 ? costTotal / effectiveDivisor : 0;

  return {
    hasRecipe: true,
    recipe,
    bom,
    costTotal,
    costUnit,
    mermaPct,
    mermaApplied,
  };
};
